<script>
export default {
	name: 'LowFareDestinationTiles',
	inheritAttrs: false,
};
</script>

<script setup>

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useEventBus } from '@vueuse/core';
import CTAButton from '~/aem-components/CTAButton.vue';
import Dropdown from '~/components/form/Dropdown.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry.js';
import { formatRoundedIntegerWithCommas } from '~/logic/helpers/string.js';
import { ref, computed, watch } from 'vue';


dayjs.extend(isSameOrAfter);

const globalBookFlightBus = useEventBus('booking-widget:book-flight');

const props = defineProps({
	title: { type: String, default: '' },
	type: { type: String, default: 'varient1' },
	destinationText: { type: String, default: '' },
	destinationUrl: { type: String, default: '' },
	isPersonalizationOn: { type: Boolean, default: false },
	fromLabel: { type: String, default: '' },

	bookDate: { type: Number, default: 0 },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	componentIdAA: { type: String, default: '' },

	economyLabel: { type: String, default: '' },
	bookNowText: { type: String, default: '' },
	exploreText: { type: String, default: '' },
	personalizationLabel: { type: String, default: '' },
	lowFareList: { type: Array, default: () => ([]) },
	destinationPriceSuffix: { type: String, default: '' },
	subtitle: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);
const originPickerEl = ref(null);
const originVal = ref('');
const pastSearches = ref([]);
const lockedItem = ref([]);

watch(
	() => props.lowFareList,
	(list) => {
		if (Array.isArray(list) && list.length) {
			originVal.value = list[0].originAirportCode || '';
		}
	},
	{ immediate: true }
);

const computedOriginList = computed(() => {
	return (props.lowFareList || []).map(({ originCityName, originAirportCode, lowFarePrice }) => ({
		displayLabel: originCityName,
		value: originAirportCode,
		lowFarePrice,
	}));
});

const originName = computed(() => {
	return props.lowFareList.length ? props.lowFareList[0].originCityName : '';
});

watch(originVal, () => {
	lockedItem.value = [];
});

const { isEditorMode, pageProperties } = useGlobalAEMState();
usePersistencyWithExpiry('flight-search:past-searches', {
	refs: { pastSearches },
});

const openFlightSearch = ({ from, to }) => {
	globalBookFlightBus.emit('active-tab-and-fill-up-form', {
		mainTab: 'flight-search-tab',
		locationFrom: from,
		locationTo: to,
	});
};

const computedCompleteDestinationList = computed(() => {
	const origin = (props.lowFareList || []).find(
		(item) => item.originAirportCode === originVal.value
	);
	return origin?.destinationList || [];
});

const modifiedList = computed(() => {
	const list = computedCompleteDestinationList.value || [];

	if (!props.isPersonalizationOn) return list;

	const result = list.map((item) => {
		let isRecommended = false;
		(pastSearches.value || []).forEach((pastSearch) => {
			if (pastSearch?.ondValueTo?.value) {
				if (
					(originVal.value === pastSearch.ondValueFrom.value) &&
					(item?.destinationAirportCode === pastSearch.ondValueTo.value)
				) {
					isRecommended = true;
				}
			}
		});
		return { ...item, isRecommended };
	});

	return result.sort((x, y) => y.isRecommended - x.isRecommended);
});

function getTileSizeClass (index) {
	const row = Math.floor(index / 3);
	const pos = index % 3;
	if (row % 2 === 0) {
		return pos === 0 ? 'big-tile' : 'small-tile';
	} else {
		return pos === 2 ? 'big-tile' : 'small-tile';
	}
}

// Helper function to check whether the tile is big/small
function isBig (index) {
	const cls = getTileSizeClass(index);
	return typeof cls === 'string' && cls.includes('big-tile');
}

// Functions to Image URL helpers 
function getPortraitUrl (item) {
	return item?.destinationTileImage || '';
}
function getLandscapeUrl (item) {
	return item?.destinationTileLandscapeImage || '';
}
</script>

<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>
			Hidden in:
			<span v-if="props.isHideInDesktop">Desktop</span>
			<span v-if="props.isHideInMobile">, Mobile</span>
		</p>
	</div>
</template>

<template v-else>
	<div class="LowFareDestinationTiles generic-container">
		<div :class="{ 'md:hidden': isHideInDesktop, 'hidden md:block': isHideInMobile }">
			
			<div class="text-primary-blue-dark flex flex-col items-center">

				<div class="flex flex-wrap items-center justify-center gap-3 sm:gap-1 px-4 pt-6 text-center">
					<!-- TITLE -->
					<h2 v-html-sanitize="props.title"></h2>

					<!-- FROM LABEL -->
					<h2 v-html-sanitize="props.fromLabel"></h2>

					<!-- DROPDOWN -->
					<Dropdown
						v-if="computedOriginList.length > 1"
						ref="originPickerEl"
						v-model="originVal"
						:rootAttrs="{ class: 'origin-picker' }"
						name="originPicker"
						:labelText="props.general?.label"
						:options="computedOriginList"
						@update:model-value="originVal = $event"
					>
						<template #placeholder>
							<div class="placeholder-wrapper relative flex-grow leading-none">
								<span class="text-label font-semibold">{{ originVal }}</span>
							</div>
						</template>

						<template #option="{ option }">
							<span class="text-value relative flex items-center">{{ option.displayLabel }}</span>
						</template>

						<template #singlelabel="{ value }">
							<div class="value-wrapper multiselect-single-label flex-grow leading-none font-semibold">
								<span class="text-value relative flex items-center">{{ value.displayLabel }}</span>
							</div>
						</template>

						<template #caret>
							<icon-fas-angle-down
								v-if="computedOriginList.length > 1"
								class="dropdown-caret >md:mt-3 md:mt-2 w-[18px] h-[18px] z-10 relative transition-transform duration-200 fill-primary-blue-dark"
							/>
						</template>

						<template #mobile-title>
							<span v-html-sanitize="props.fromLabel"></span>
						</template>
					</Dropdown>

					<!-- If no dropdown -->
					<h2 v-else v-html-sanitize="originName || 'Kuala Lumpur'"></h2>
				</div>
				<p v-html-sanitize="props.subtitle" class="mb-3 md:px-4 text-center text-primary-black-base"></p>
			</div>
			
			<!-- Variant 1 -->
			<div v-if="props.type === 'varient1'" class="low-fair-container-variant1">
				<div class="stack-container pb-4">
					<!-- Highlight tile (treat as BIG = LANDSCAPE only) -->
					<div v-if="modifiedList.length" class="w-1/2 col-highlight md:hidden">
						<div
							:class="{ 'locked': lockedItem.includes('highlight') && !isMobileViewport }"
							class="low-fare-dest-tiles-wrapper group"
							@click="toggleLock('highlight')"
						>
							<div class="low-fare-dest-tiles-content-container">
								
								<div
									class="low-fare-dest-tiles-content flex flex-col items-start justify-between text-neutral-white-base px-7 py-6"
									:style="{
										'background-image': `url('${getLandscapeUrl(modifiedList[0])}')`,
									}"
									:title="`${modifiedList[0]?.destinationName} (${ modifiedList[0]?.destinationAirportCode })`"
								>
									<div class="inner-tile-overlay"></div>
									<span class="sr-only">
										{{ modifiedList[0]?.destinationName }} ({{ modifiedList[0]?.destinationAirportCode }})
									</span>

									<div
										class="low-fare-dest-tiles-content-tag"
										:class="{ 'invisible': !modifiedList[0]?.isRecommended }"
									>
										<div>
											<p class="line-clamp-1">{{ props.personalizationLabel || $t('Recommended For You') }}</p>
										</div>
									</div>

									<div class="tile-text-content w-full">
										<div class="tile-text-wrapper">
											<p class="destination-title heading-4 md:heading-6">{{ modifiedList[0]?.destinationName }}</p>
											<p
												:class="{ 'invisible': parseInt(modifiedList[0]?.lowFarePrice) <= 0 }"
												class="cabin-class-txt font-semibold"
											>
												{{ props.economyLabel }}
											</p>
											<p
												:class="{ 'invisible': parseInt(modifiedList[0]?.lowFarePrice) <= 0 }"
												class="price-txt heading-4 md:heading-6"
											>
												{{ modifiedList[0]?.currencyCode }} {{ formatRoundedIntegerWithCommas(modifiedList[0]?.lowFarePrice) }}
												<span class="low-fare-only-1">{{ $i18n.locale === 'en' ? props.destinationPriceSuffix : '' }}</span>
											</p>
										</div>

										<div class="low-fare-dest-tiles-content-btn-group">
											<!-- Book Now -->
											<CTAButton
												v-aa="[
													{
														clickName: props.bookNowText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: modifiedList[0]?.destinationAirportCode,
													},
													{ type: 'other', name: props.bookNowText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -1px;"
												styleType="primary"
												:ctaText="props.bookNowText"
												@click="openFlightSearch({ from: originVal, to: modifiedList[0]?.destinationAirportCode })"
											/>
											<!-- Explore -->
											<CTAButton
												v-aa="[
													{
														clickName: props.exploreText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: modifiedList[0]?.destinationAirportCode,
													},
													{ type: 'other', name: props.exploreText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -2px;"
												styleType="secondary"
												:ctaText="props.exploreText"
												:href="modifiedList[0]?.destinationDetailsPageUrl"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Stack or grid tiles (treat as SMALL = PORTRAIT only) -->
					<div class="w-1/2 md:w-full col-stack-with-grid">
						<div
							v-for="(item, index) in (isMobileViewport ? modifiedList : modifiedList.slice(1))"
							:key="index"
							:class="{
								'hidden': index >= 6,
								'locked': lockedItem.includes(`${originVal}${index}`) && !isMobileViewport,
							}"
							class="low-fare-dest-tiles-wrapper group"
							@click="toggleLock(`${originVal}${index}`)"
						>
							<div class="low-fare-dest-tiles-content-container">
								
								<div
									class="low-fare-dest-tiles-content flex flex-col items-start justify-between text-neutral-white-base px-7 py-6 lg:p-4"
									:style="{
										'background-image': `url('${getPortraitUrl(item)}')`,
									}"
									:title="`${item.destinationName} (${ item.destinationAirportCode })`"
								>
									<div class="inner-tile-overlay"></div>
									<span class="sr-only">{{ item.destinationName }} ({{ item.destinationAirportCode }})</span>

									<div class="low-fare-dest-tiles-content-tag invisible">
										<div>
											<p class="line-clamp-1">{{ props.personalizationLabel || $t('Recommended For You') }}</p>
										</div>
									</div>

									<div class="tile-text-content w-full">
										<div class="tile-text-wrapper">
											<p class="destination-title heading-4 md:heading-6">{{ item.destinationName }}</p>
											<p
												:class="{ 'invisible': parseInt(item.lowFarePrice) <= 0 }"
												class="cabin-class-txt font-semibold"
											>
												{{ props.economyLabel }}
											</p>
											<p
												:class="{ 'invisible': parseInt(item.lowFarePrice) <= 0 }"
												class="price-txt heading-4 md:heading-6"
											>
												{{ item.currencyCode }} {{ formatRoundedIntegerWithCommas(item.lowFarePrice) }}
												<span class="low-fare-only-2">{{ $i18n.locale === 'en' ? props.destinationPriceSuffix : '' }}</span>
											</p>
										</div>

										<div class="low-fare-dest-tiles-content-btn-group">
											<!-- Book Now -->
											<CTAButton
												v-aa="[
													{
														clickName: props.bookNowText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{ type: 'other', name: props.bookNowText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -1px;"
												styleType="primary"
												:ctaText="props.bookNowText"
												@click="openFlightSearch({ from: originVal, to: item.destinationAirportCode })"
											/>
											<!-- Explore -->
											<CTAButton
												v-aa="[
													{
														clickName: props.exploreText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{ type: 'other', name: props.exploreText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -2px;"
												styleType="secondary"
												:ctaText="props.exploreText"
												:href="item.destinationDetailsPageUrl"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Bottom CTA -->
				<CTAButton
					containerStyle="--focus-visible-outline-offset: -2px;"
					containerClass="pt-10 pb-4"
					:ctaText="props.destinationText"
					:href="props.destinationUrl"
					sizeType="medium"
					styleType="primary"
				/>
			</div>

			<!-- Variant 2 -->
			<div v-if="props.type === 'varient2'" class="low-fair-container-variant2">
				<div class="stack-container pb-4">
					<div class="w-1/1 md:w-full col-stack-with-grid">
						<div
							v-for="(item, index) in (isMobileViewport ? modifiedList : modifiedList.slice())"
							:key="index"
							:class="[
								{
									'locked': lockedItem.includes(`${originVal}${index}`) && !isMobileViewport,
								},
								getTileSizeClass(index), // returns 'big-tile' or 'small-tile'
							]"
							class="low-fare-dest-tiles-wrapper"
							@click="toggleLock(`${originVal}${index}`)"
						>
							<div class="low-fare-dest-tiles-content-container ">
								
								<div
									class="low-fare-dest-tiles-content flex flex-col items-start justify-between text-neutral-white-base px-7 py-6 lg:p-4"
									:data-size="isBig(index) ? 'big' : 'small'"
									:style="{
										'background-image': `url('${ isBig(index) ? getLandscapeUrl(item) : getPortraitUrl(item) }')`,
									}"
									:title="`${item.destinationName} (${ item.destinationAirportCode })`"
								>
									<div class="inner-tile-overlay"></div>
									<span class="sr-only">{{ item.destinationName }} ({{ item.destinationAirportCode }})</span>

									<div
										class="low-fare-dest-tiles-content-tag"
										:class="{ 'invisible': !item.isRecommended }"
									>
										<div>
											<p class="line-clamp-1  m-0">{{ props.personalizationLabel || $t('Recommended For You') }}</p>
										</div>
									</div>

									<div class="tile-text-content w-full">
										<div class="tile-text-wrapper">
											<p class="destination-title m-0 heading-4 md:heading-6">{{ item.destinationName }}</p>
											<p
												:class="{ 'invisible': parseInt(item.lowFarePrice) <= 0 }"
												class="cabin-class-txt font-semibold m-0"
											>
												{{ props.economyLabel }}
											</p>
											<p
												:class="{ 'invisible': parseInt(item.lowFarePrice) <= 0 }"
												class="price-txt m-0 heading-4 md:heading-6"
											>
												{{ item.currencyCode }} {{ formatRoundedIntegerWithCommas(item.lowFarePrice) }}
												<span class="low-fare-only-2">{{ $i18n.locale === 'en' ? props.destinationPriceSuffix : '' }}</span>
											</p>
										</div>

										<div class="low-fare-dest-tiles-content-btn-group">
											<!-- Book Now -->
											<CTAButton
												v-aa="[
													{
														clickName: props.bookNowText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{ type: 'other', name: props.bookNowText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -1px;"
												styleType="primary"
												:ctaText="props.bookNowText"
												@click="openFlightSearch({ from: originVal, to: item.destinationAirportCode })"
											/>
											<!-- Explore -->
											<CTAButton
												v-aa="[
													{
														clickName: props.exploreText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{ type: 'other', name: props.exploreText },
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -2px;"
												styleType="secondary"
												:ctaText="props.exploreText"
												:href="item.destinationDetailsPageUrl"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Bottom CTA -->
				<CTAButton
					containerStyle="--focus-visible-outline-offset: -2px;"
					containerClass="pt-10 pb-4"
					:ctaText="props.destinationText"
					:href="props.destinationUrl"
					sizeType="medium"
					styleType="primary"
				/>
			</div>
		</div>
	</div>
</template>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';
@use '~/styles/partials/_mixins.scss';

.LowFareDestinationTiles {
	:deep(.tile-text-content),
	:deep(.low-fare-dest-tiles-content-tag),
	:deep(.tile-text-wrapper) {
		position: relative;
		z-index: 5;
}
	&.generic-container {
		@include mixins.glassTabContainer;
	}

  .low-fare-dest-tiles-content {
    position: relative !important;
  }

  .tile-text-wrapper {
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 10px;

    display: flex;
    flex-direction: column;
    gap: 2px;

    z-index: 10;
    transition: all 0.35s ease; /* smooth animation */
  }

  .low-fare-dest-tiles-wrapper:hover .tile-text-wrapper,
  .low-fare-dest-tiles-wrapper.locked .tile-text-wrapper {
    transform: translateY(-95px);
  }

  .tile-text-content {
    position: relative !important;
    width: 100%;
  }

  @media #{var.$query-max-md} {
				
	.title-text {
		font-size: 20px;
	}
}

  :deep(.origin-picker) {
    margin-top: -1px;

    .multiselect {
      background: transparent;
      border: 0;
    }
    .multiselect-search-wrapper { position: static; }

    .multiselect-main {
      height: auto;

      .text-value { top: 0; }

      .multiselect-single-label {
        position: static;
        padding: 0 !important;
        margin: 0;
        margin-right: 5px;
        border-bottom: 2px solid var(--primary-blue-dark);;

        .text-value {
			top: 1px !important;
			font-weight: 700;
			text-align: center;
			font-size: 2.5rem;
			line-height: 3rem;

			@media #{var.$query-max-xl} {
				font-size: 2rem;
				line-height: 2.5rem;
			}
			@media #{var.$query-max-md} {
				font-size: 1.75rem;
				line-height: 2.25rem;
			}
		}
      }
    }

    .multiselect-dropdown {
		width: 290px !important;
		z-index: 1;
	}
  }

  .low-fare-dest-tiles-content {
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover !important;
    border-radius: inherit;
    transition: transform 1s cubic-bezier(.2, .7, .2, 1);
    will-change: transform;
  }

  .low-fare-dest-tiles-wrapper:hover .low-fare-dest-tiles-content,
  .low-fare-dest-tiles-wrapper:focus-within .low-fare-dest-tiles-content,
  .low-fare-dest-tiles-wrapper.locked .low-fare-dest-tiles-content {
    transform: scale(1.08);
  }

  @media (prefers-reduced-motion: reduce) {
    .low-fare-dest-tiles-content {
      transition: none !important;
    }
  }

  .inner-tile-overlay {
    @apply mx-auto inset-x-0;
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
    border-radius: inherit;
    opacity: 1;
    transition: opacity 400ms ease;
    background: linear-gradient(
      to top,
      rgba(4, 57, 119, 0.97) 0%,
      rgba(4, 57, 119, 0.05) 25%,
      rgba(4, 57, 119, 0.10) 55%,
      rgba(4, 57, 119, 0.00) 75%
    );
  }

  /*  VARIANT 1  */
  .low-fair-container-variant1 {
    .low-fare-dest-tiles-content-btn-group {
      @apply flex flex-col gap-3 lg:gap-[10px] w-full mt-3;
      visibility: hidden;
      opacity: 0;
      margin-bottom: -50px;
      transition: all ease 400ms; position: relative; z-index: 10;

      :deep(.CTAButton) {
        @apply text-sm lg:text-xs py-3 lg:py-1.5 px-4 w-full font-semibold;
      }
    }

    .col-stack-with-grid .low-fare-dest-tiles-content-btn-group {
      margin-bottom: -125px;
      @media #{var.$query-max-lg} { margin-bottom: -80px; }
    }

    :deep(.tile-text-content),
    :deep(.low-fare-dest-tiles-content-tag) {
      position: relative;
      z-index: 2;
    }

    .stack-container { @apply flex gap-6; }

    .low-fare-dest-tiles-wrapper {
      position: relative;
      width: 100%;
      aspect-ratio: 1/1.25;
      user-select: none;
      transition: filter .5s;
      filter: drop-shadow(0 3px 7px transparent);

      @media #{var.$query-min-md} { aspect-ratio: 1/1.5; }
      @media #{var.$query-min-lg} { aspect-ratio: 1/1.25; }
      @media #{var.$query-min-xl} { aspect-ratio: 1/1; }

      &:hover,
      &:focus-within,
      &.locked {
        .inner-tile-overlay { opacity: 1; }
        :deep(.low-fare-dest-tiles-content-btn-group) {
          visibility: visible;
          opacity: 1;
          margin-bottom: 0px;
        }
      }
    }

    .low-fare-dest-tiles-content-container {
      @apply inset-x-0;
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 24px;

      @media #{var.$query-max-lg} { border-radius: 12px; }
    }

    .low-fare-dest-tiles-content-tag {
      @apply bg-neutral-white-base text-primary-blue-base rounded px-2 text-xs font-bold py-0.5;
    }

    .destination-title { @apply line-clamp-2; }
    .cabin-class-txt { @apply line-clamp-1; }
    .price-txt { @apply line-clamp-1; }

    .col-highlight {
      .low-fare-dest-tiles-wrapper { height: 100%; }
      .low-fare-dest-tiles-content-btn-group { @apply flex-row; }
    }

    .col-stack-with-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      max-width: 1200px;
      margin: 0 auto;
    }
  }

  /*  VARIANT 2  */
  .low-fair-container-variant2 {
    .low-fare-dest-tiles-content-btn-group {
      @apply flex flex-col gap-3 lg:gap-[10px] w-full mt-3;
      visibility: hidden;
      opacity: 0;
      margin-bottom: -50px;
      transition: all ease 400ms; position: relative; z-index: 10;

      :deep(.CTAButton) {
        @apply text-sm lg:text-xs py-3 lg:py-1.5 px-4 w-full font-semibold;
      }
    }

    .low-fare-dest-tiles-wrapper {
      height: 300px;
      position: relative;
      display: flex;
      flex-direction: column;
      user-select: none;

      &:hover,
      &:focus-within,
      &.locked {
        .inner-tile-overlay { opacity: 1; }
        :deep(.low-fare-dest-tiles-content-btn-group) {
          visibility: visible;
          opacity: 1;
          margin-bottom: 0px;
        }
      }
    }

    .low-fare-dest-tiles-content-container {
      @apply inset-x-0;
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 24px;

      @media (max-width: 767px) {
        border-radius: 12px;
      }
    }

    .big-tile { grid-column: span 2; }
    .small-tile { grid-column: span 1; }

    .col-stack-with-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      align-items: stretch;
      grid-auto-flow: dense;

      @media (max-width: 767px) {
        grid-template-columns: repeat(2, 1fr);
        .big-tile { grid-column: span 1; }
      }
    }
  }

  .low-fare-only-1 { font-size: 30px; font-weight: normal; }
  .low-fare-only-2 { font-size: 20px; font-weight: normal; }

}
</style>